import { FC, ReactNode } from "react";

import { Button } from "@/components/_atoms/button";
import { IconPlus } from "@/components/_icons/IconPlus";
import { ExplanationTooltip } from "@/components/_molecules/explanation-tooltip";

export interface TableEmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  action?: ReactNode;
  // When set, the default action button is disabled and, if actionTooltip is
  // given, explains why on hover (e.g. a guest who may not create content).
  actionDisabled?: boolean;
  actionTooltip?: string;
}

export const TableEmptyState: FC<TableEmptyStateProps> = ({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  action,
  actionDisabled = false,
  actionTooltip,
}) => {
  const hasButtonAction = actionLabel !== undefined && onAction !== undefined;

  const button = hasButtonAction ? (
    <Button onClick={onAction} disabled={actionDisabled} className="mt-1 shadow-xs">
      <IconPlus className="mr-2 h-4 w-4" aria-hidden="true" />
      {actionLabel}
    </Button>
  ) : null;

  // A disabled <Button> cannot receive focus, so keyboard users could neither
  // reach the explanation nor learn why the action is unavailable. Wrap it in a
  // focusable, labelled trigger: the button stays truly inert while the tooltip
  // opens on both hover and keyboard focus.
  const actionNode =
    button && actionDisabled && actionTooltip ? (
      <ExplanationTooltip content={actionTooltip}>
        <span tabIndex={0} aria-disabled="true" aria-label={`${actionLabel}: ${actionTooltip}`} className="inline-flex">
          {button}
        </span>
      </ExplanationTooltip>
    ) : (
      button
    );

  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-4 px-6 py-16 text-center"
      data-testid="table-empty-state"
    >
      {icon && (
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          {icon}
        </div>
      )}
      <div className="space-y-1.5">
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        {description && <p className="mx-auto max-w-md text-sm text-muted-foreground">{description}</p>}
      </div>
      {action ?? actionNode}
    </div>
  );
};
